import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import tradeDataStore from "@/store/tradeDataStore";
import { updateExecutionEditSecurity } from "@/store/tradeDataStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  isStrikeEditable,
  returnEridFromBlock,
} from "../actionSummaryColDefHelperFunctions";
import { isValidStirke } from "@/lib/businessRules/strikeValueCells";
import { truncateValueIfNeeded } from "./DynamicCellEditorQuoteDollarFractionHelper";

import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import { useTheme } from "next-themes";

const StrikeCellEditor = forwardRef((params: any, ref) => {
  const { getAccessTokenSilently } = useAuth0();
  const { theme } = useTheme();

  const data = params.data;
  const fullTradeData = params.colDef.cellRendererParams.fullTradeData;
  const isOutright = fullTradeData.strategyType == StrategyType.Outright;
  const isOp = fullTradeData.role == "OP";

  const currentTradeData = tradeDataStore.getState().tradeData;
  const thisTrade = currentTradeData.find(
    (trade) => trade.esid == fullTradeData.esid,
  );

  function setErcpIndex(): number {
    if (isOp) {
      thisTrade?.executionRequestCounterParties!.forEach((trade, index) => {
        if (trade.executionRequestId == data.executionRequestId) {
          return index;
        }
      });
    }
    return 0;
  }

  const ercpIndex = setErcpIndex();

  let valueToReturn: string | number | undefined = "";
  if (thisTrade && thisTrade.executionRequestCounterParties) {
    valueToReturn =
      thisTrade?.executionRequestCounterParties?.[ercpIndex]?.dealtStrike || "";
    if (isOp && isOutright) {
      valueToReturn =
        thisTrade?.executionRequestCounterParties?.[ercpIndex]?.legs?.[0]
          ?.dealtStrike;
      // LEG DEALT STRIKE
    } else if (!isOutright) {
      valueToReturn = data.dealtStrike;
    }
  }

  const [value, setValue] = useState(valueToReturn || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const notation: NotationToDecimalValueType = "strike";

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (!isValidStirke(event.target.value)) {
      return; // return early and don't set the value if the input does not match rules.
    }
    const inputEvent = event.nativeEvent as InputEvent;

    if (input == "") {
      if (inputEvent.inputType != "deleteContentBackward") {
        return;
      }
      setValue(input);
      return;
    }

    setValue(input);
  };

  const validateAndSave = () => {
    const valueHasNotBeenChanged = valueToReturn == value;
    if (valueHasNotBeenChanged) {
      return Number(value);
    }

    //return early if the input value is the same as starting value.
    if (Number(value) < 0 || value === "") {
      toast.error(`Strike value is too small and set back to ${valueToReturn}`);
      return valueToReturn;
    } else if (valueToReturn == value) {
      return Number(value);
    }
    if (Number(value) > 900) {
      toast.error(
        `Strike value exceeds maximum of 900 and set back to ${valueToReturn}`,
      );
      return Number(valueToReturn);
    }

    const updatedValue = truncateValueIfNeeded(value, notation).replace(
      /,/g,
      "",
    );

    if (isOp && isOutright) {
      updateExecutionEditSecurity({
        esid: fullTradeData.esid,
        erid: params.data.executionRequestId,
        blockid: params.data.legs?.[0]?.blockId,
        deliveryDate: params.data.legs?.[0]?.deliveryDate,
        dealtStrike: Number(updatedValue),
        getAccessTokenSilently: getAccessTokenSilently,
      });
      // UPDATE on the Block Level for StructureTypes that are accepted.
    } else if (isOp && !isOutright) {
      updateExecutionEditSecurity({
        esid: fullTradeData.esid,
        erid: returnEridFromBlock(fullTradeData, params.data.blockId),
        blockid: params.data.blockId,
        deliveryDate: params.data.deliveryDate,
        dealtStrike: Number(updatedValue),
        getAccessTokenSilently: getAccessTokenSilently,
      });
    }
    return Number(updatedValue);
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  const isEditable = isStrikeEditable({
    params: params,
    esid: fullTradeData?.esid,
    thisTradePassed: thisTrade,
  });

  function editableStyle() {
    if (isEditable && theme == "light") {
      return "inputCellOtherLight roundedInputCell";
    } else if (isEditable && theme == "dark") {
      return "inputCellOtherDark roundedInputCell";
    }
  }

  return (
    <input
      ref={inputRef}
      type="number"
      value={
        value == valueToReturn
          ? truncateValueIfNeeded(value.toString(), notation)
          : value
      }
      onChange={handleInput} // Blocks unwanted characters immediately
      className={`ag-roundedInputStyle ag-input text-center ${editableStyle()}`}
    />
  );
});

export default StrikeCellEditor;
